import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { replaceUserEndpoint } from '../utils/userEndpoint';

const ThemeContext = createContext();
const THEME_STORAGE_KEY = 'ff_is_dark_mode_enabled';

function readStoredTheme() {
	try {
		const stored = localStorage.getItem(THEME_STORAGE_KEY);
		if (stored === null) return null;
		return stored === '1';
	} catch {
		return null;
	}
}

function writeStoredTheme(darkMode) {
	try {
		localStorage.setItem(THEME_STORAGE_KEY, darkMode ? '1' : '0');
	} catch {
		// localStorage unavailable (private browsing, etc.)
	}
}

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};

export const ThemeProvider = ({ children, updateThemeEndpoint, updateFitnessProfileEndpoint }) => {
	const { user, csrfToken, activeFitnessProfile, patchActiveFitnessProfile } = useAuth();
	const isHouseholdSession = Boolean(activeFitnessProfile);
	const savedDarkMode = isHouseholdSession
		? activeFitnessProfile?.is_dark_mode_enabled
		: user?.is_dark_mode_enabled;
	const [
		isDarkMode,
		setIsDarkMode
	] = useState(() => {
		try {
			return document.documentElement.classList.contains('dark');
		} catch {
			return readStoredTheme() === true;
		}
	});
	const [
		isLoading,
		setIsLoading
	] = useState(true);

	useEffect(
		() => {
			const initializeTheme = () => {
				let darkMode = false;

				if (savedDarkMode !== undefined) {
					darkMode = Boolean(savedDarkMode);
					writeStoredTheme(darkMode);
				} else {
					const storedTheme = readStoredTheme();
					if (storedTheme !== null) {
						darkMode = storedTheme;
					}
				}

				setIsDarkMode(darkMode);
				applyTheme(darkMode);
				setIsLoading(false);
			};

			initializeTheme();
		},
		[
			savedDarkMode
		]
	);

	const applyTheme = (darkMode) => {
		if (darkMode) {
			document.documentElement.classList.add('dark');
		} else {
			document.documentElement.classList.remove('dark');
		}
		writeStoredTheme(darkMode);
	};

	const persistHouseholdTheme = useCallback(
		async (newTheme) => {
			if (!user?.id || !activeFitnessProfile?.id || !updateFitnessProfileEndpoint) {
				return false;
			}

			const url = replaceUserEndpoint(updateFitnessProfileEndpoint, {
				userId: user.id,
				fitnessProfileId: activeFitnessProfile.id
			});
			const response = await fetch(url, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				credentials: 'same-origin',
				body: JSON.stringify({
					fitness_profile: {
						is_dark_mode_enabled: newTheme
					}
				})
			});

			if (!response.ok) return false;

			patchActiveFitnessProfile({ is_dark_mode_enabled: newTheme });
			return true;
		},
		[
			activeFitnessProfile?.id,
			csrfToken,
			patchActiveFitnessProfile,
			updateFitnessProfileEndpoint,
			user?.id
		]
	);

	const persistOwnerTheme = useCallback(
		async (newTheme) => {
			if (!user?.id || !updateThemeEndpoint) return false;

			const response = await fetch(replaceUserEndpoint(updateThemeEndpoint, { userId: user.id }), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				credentials: 'same-origin',
				body: JSON.stringify({
					user: {
						is_dark_mode_enabled: newTheme
					}
				})
			});

			return response.ok;
		},
		[csrfToken, updateThemeEndpoint, user?.id]
	);

	const toggleTheme = async () => {
		const newTheme = !isDarkMode;
		setIsDarkMode(newTheme);
		applyTheme(newTheme);

		if (!user) return;

		try {
			const saved = isHouseholdSession
				? await persistHouseholdTheme(newTheme)
				: await persistOwnerTheme(newTheme);

			if (!saved) {
				console.error('Failed to update theme preference');
				setIsDarkMode(!newTheme);
				applyTheme(!newTheme);
			}
		} catch (error) {
			console.error('Error updating theme preference:', error);
			setIsDarkMode(!newTheme);
			applyTheme(!newTheme);
		}
	};

	const value = {
		isDarkMode,
		toggleTheme,
		isLoading
	};

	return (
		<ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
	);
};
